import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "antd";
import { pick, toLower } from "lodash";
import { Flex } from "../../components/Flex";
import { Page404 } from "../../components/Page404";
import { Paragraph, Title } from "../../components/Text";
import { activateContract, changeUserAccess, deleteAttachment, deleteOrgContract, deletePackage, deleteUser, downloadAttachment, getDetails, revokeGrant, saveIssueComment, updateAttachment, verifyPackage } from "../../store/dataSlice";
import { editValues, showForm } from "../../store/formSlice";
import { downloadPackage } from "../../store/uploadSlice";
import { IssueDetails } from "./IssueDetails";
import { OrgDetails } from "./OrgDetails";
import { PkgDetails } from "./PkgDetails";
import { UserDetails } from "./UserDetails";
import { pingServer } from "../../store/userSlice";
import { ContractsDetails } from "./ContractsDetails";
import { AuditDetails } from "./AuditDetails";

export const confirmModalOptions = ({
    centered: true,
    icon: null,
    className: 'sp-confirm-modal',
    title: <Title h4 color="#E8AF1D">Attention!</Title>,
    okText: "Delete",
    okButtonProps: { style: { background: "#e96666", borderRadius: "4px", border: "none", fontWeight: "600" } },
    cancelButtonProps: { type: "link", style: { fontWeight: "600", fontSize: "14px", color: "#1d65db" } },
    onCancel() { },
});

const DetailsContainer = ({ match, location, history }) => {
    const { pathname, search } = location;
    const { org, tab, selected } = match.params;
    const page = pathname.split("/")[1];
    const dispatch = useDispatch();
    const { loading, details, notFound } = useSelector(state => state.data);
    const { visible, hasChanges } = useSelector(state => state.form);
    const { uploads } = useSelector(state => state.upload);
    const { contractsWrite, packageWrite, userDelete } = useSelector(state => state.user.grants)

    useEffect(() => {
        if (page === "audit") {
            dispatch(getDetails({ page: page, tab: tab, selected: selected }));
        } else if (tab) {
            dispatch(getDetails({ page: tab, selected: selected }));
        } else if (org) {
            dispatch(getDetails({ page: page, org: org, selected: org }));
        };
    }, [ org, tab, selected, dispatch ]);

    const checkChanges = (key, values) => {
        if (key !== visible) {
            if (hasChanges) {
                Modal.confirm({
                    centered: true,
                    icon: null,
                    className: "sp-confirm-modal",
                    title: <Title h4 color="#E8AF1D">Warning</Title>,
                    content: <Paragraph>Changes will not be saved. Are you sure you want to close the form?</Paragraph>,
                    okText: "Close",
                    okButtonProps: {style: { height: "40px", padding: "0px 21px", background: "var(--sp-main-color)", borderRadius: "8px", border: "none" }},
                    cancelButtonProps: {type: "link", style:{ fontSize: "14px", color: "var(--sp-main-color)" }},
                    onOk() {
                        onFormShow(key, values);
                    },
                    onCancel() {}
                });
            } else {
                onFormShow(key, values);
            }
        };
    };

    const onFormShow = (key, values) => {
        if (values) {
            dispatch(editValues({ entity: key, values: values }));
        } else {
            dispatch(showForm(key));
        };
    };

    const confirmRemovingFile = (id, name) => {
        Modal.confirm({
            ...confirmModalOptions,
            content: <Paragraph>Are you sure you want to delete <Paragraph bold>{name}</Paragraph>?</Paragraph>,
            onOk: () => tab === "issues" ? dispatch(deleteAttachment({ issue: selected, id: id })) : dispatch(deletePackage(id))
        });
    };

    const renderFieldContent = (attributes, data) => {
        return (attributes.map((attribute, idx) => (
            <Flex key={`attribute-${idx}`} gap={{ right: 3 }} align="center">
                <Paragraph color="var(--sp-label-color)" lineHeight="22px">{attribute.label}:</Paragraph>
                <Paragraph isLoading={loading}>
                    {data && data.hasOwnProperty(attribute.attr) && data[attribute.attr] !== null && data[attribute.attr] !== undefined
                        ? attribute.render
                            ? attribute.render(data[attribute.attr])
                            : data[attribute.attr]
                        : "-"
                    }
                </Paragraph>
            </Flex>
        )))
    };

    const confirmRemovingContract = ({ alias, contract, org }) => {
        Modal.confirm({
            ...confirmModalOptions,
            content: <Paragraph>Are you sure you want to delete the <Paragraph bold>{contract}</Paragraph> contract for <Paragraph bold>{org}</Paragraph>?</Paragraph>,
            onOk: () => dispatch(deleteOrgContract({ alias: alias, contract: toLower(contract) }))
        });
    };

    const confirmRemovingUser = (email) => {
        Modal.confirm({
            ...confirmModalOptions,
            content: <Paragraph>Are you sure you want to delete the <Paragraph bold>{email}</Paragraph> without the possibility of recovery?</Paragraph>,
            onOk: () => dispatch(deleteUser(email))
        });
    };

    const onClose = (e) => {
        e.preventDefault();
        history.push(pathname.substring(0, pathname.lastIndexOf("/")) + search);
    };

    const content = {
        organisations: (
            <OrgDetails
                data={details}
                loading={loading}
                showForm={checkChanges}
                renderFieldContent={renderFieldContent}
                editContracts={(values) => onFormShow("contracts", values)}
                deleteContract={(values) => confirmRemovingContract(values)}
            />
        ),
        users: (
            <UserDetails
                title={details.name}
                data={details}
                loading={loading}
                issueGrant={() => dispatch(showForm("grants"))}
                editInfo = {onFormShow}
                revokeGrant = {(values) => dispatch(revokeGrant(values))}
                renderFieldContent={renderFieldContent}
                changeUserAccess={(values) => dispatch(changeUserAccess(values))}
                onClose={onClose}
                deleteUser={userDelete ? confirmRemovingUser : null}
            />
        ),
        issues: (
            <IssueDetails
                title={details.ticketRef}
                data={details}
                loading={loading}
                renderFieldContent={renderFieldContent}
                saveComment={values => dispatch(saveIssueComment({ issue: selected, ...values }))}
                onClose={onClose}
                pingServer={() => dispatch(pingServer())}
                downloadAttachment={(id) => dispatch(downloadAttachment({ issue: selected, id: id }))}
                deleteAttachment={(id, name) => confirmRemovingFile(id, name)}
                updateAttachment={(id, visible) => dispatch(updateAttachment({ issue: selected, id: id, visible: visible }))}
            />
        ),
        packages: (
            <PkgDetails
                title={details.name}
                data={details}
                loading={loading}
                isContractsWrite={contractsWrite}
                isPackageWrite={packageWrite}
                uploadProgress={Array.isArray(details.files) ? pick(uploads, details.files.map(file => file.key)) : {}}
                editInfo={(entity, values) => onFormShow(entity, values)}
                verifyPackage={() => dispatch(verifyPackage({ pkgId: selected, verify: details.verified == 0 }))}
                removePackage={() => confirmRemovingFile(selected, details.name)}
                downloadPackage={(values) => dispatch(downloadPackage(values))}
                renderFieldContent={renderFieldContent}
                onClose={onClose}
            />
        ),
        contracts: (
            <ContractsDetails
                data={details}
                loading={loading}
                onClose={onClose}
                title={details.name}
                renderFieldContent={renderFieldContent}
                deleteContract={(values) => confirmRemovingContract(values)}
                activateContract={(values) => dispatch(activateContract(values))}
            />
        ),
        download: (
            <AuditDetails
                data={details}
                loading={loading}
                onClose={onClose}
                title="Package Download Audit Details"
                renderFieldContent={renderFieldContent}
            />
        )
    };

    return(
        notFound || (!org && Object.keys(content).indexOf(tab) < 0)
            ? <Page404 />
            : tab
                ? content[tab]
                : org ? content.organisations : null
    );
};

export { DetailsContainer };